import { useState } from 'react'

import { api, ApiError } from '../api/client.js'
import type { SessionUser } from '../auth/session-context.js'
import { Button } from '../components/Button.js'
import { Input } from '../components/Input.js'

export interface ResetPasswordProps {
  readonly account: SessionUser
  /** Whether this is the parent's own account, which changes what happens. */
  readonly isSelf: boolean
}

/**
 * A new password for somebody who has forgotten theirs.
 *
 * A child forgetting her password had no answer inside the app at all. The
 * server has been able to do this the whole time — a parent may change any
 * account, a child only their own, roles are not changeable through any
 * request shape, and the hashing is the same as at sign-up — so this is the
 * missing door rather than a missing room. It reuses `PATCH /api/users/:id`
 * exactly as the profile screen does; nothing here is a new way in.
 *
 * **Says what it will do before it does it**, because it does something the
 * person typing cannot see: every session for that account is revoked. A child
 * suddenly signed out on the tablet with nobody having said so is a bug report
 * waiting to happen. The exception is a parent resetting their own, where the
 * server issues them a fresh session rather than logging them out mid-sentence
 * — so the sentence differs, since only one of them is true at a time.
 *
 * The password is never echoed back, never put in a URL, and is dropped from
 * state the moment it has been sent.
 */
export function ResetPassword({ account, isSelf }: ResetPasswordProps) {
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function submit(event: React.FormEvent): void {
    event.preventDefault()
    setSaving(true)
    setError(null)

    void api(`/api/users/${account.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ password }),
    })
      .then(() => {
        // Forgotten as soon as it is sent. There is nothing this screen wants
        // it for afterwards, and the shortest life is the safest one.
        setPassword('')
        setOpen(false)
        setDone(true)
      })
      .catch((caught: unknown) => {
        setError(
          caught instanceof ApiError ? caught.message : 'Could not change that password just now.',
        )
      })
      .finally(() => setSaving(false))
  }

  if (!open) {
    return (
      <div className="flex flex-col items-start gap-1">
        <Button
          variant="secondary"
          onClick={() => {
            setOpen(true)
            setDone(false)
          }}
        >
          {`New password for ${account.displayName}`}
        </Button>

        {done ? (
          <p role="status" className="text-teal text-(length:--text-small)">
            {isSelf
              ? 'Done. Your other devices have been signed out.'
              : `Done. ${account.displayName} has been signed out everywhere and will need the new password.`}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="flex w-full flex-col gap-3">
      <Input
        label={`New password for ${account.displayName}`}
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        hint={
          isSelf
            ? 'Your other devices will be signed out. This one stays signed in.'
            : `${account.displayName} will be signed out on every device.`
        }
        autoComplete="new-password"
        required
      />

      {error ? (
        <p role="alert" className="text-coral text-(length:--text-small)">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <Button type="submit" disabled={saving || password === ''}>
          {saving ? 'Changing…' : 'Change it'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setPassword('')
            setError(null)
            setOpen(false)
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}
